import React from 'react';
import { Link } from 'react-router-dom';

import {
  finleyAssert,
  finleyAssertUnreachable,
} from '@finley-technologies/common';

import { drawerErrorExplainers } from './domainActions';
import { useUploadContext } from '~/packages/dataPipeline/hooks/UploadContext';
import { CustomError } from '~/packages/dataPipeline/types/dataPipelineTypes';
import { ActionableAlert } from '~/packages/dataPipeline/ui/components/ActionableAlert';

export const UploadDrawerAlert = () => {
  const {
    canUpload,
    completeRecord,
    meshRunId,
    instances,
  } = useUploadContext();

  if (!canUpload) {
    return (
      <ActionableAlert status="info" mb="10px">
        Pipeline changed. You can not upload files to this pipeline.
      </ActionableAlert>
    );
  }

  if (!meshRunId) {
    return null;
  }

  // since mesRunId is available we can assume that completeRecord is available
  finleyAssert(completeRecord, 'completeRecord must be available');
  const status = completeRecord.meshRunRecord.status;
  // the run's page is linked from its progress box (DrawerProgressBar)
  switch (status) {
    case 'inProgress':
      return (
        <ActionableAlert status="info" mb="10px">
          Pipeline in progress
        </ActionableAlert>
      );

    case 'error': {
      const run = {
        artifacts: completeRecord.meshDocument.artifacts,
        instances,
      };
      const customError = drawerErrorExplainers.reduce<CustomError | null>(
        (found, explain) => found ?? explain(run),
        null,
      ) ?? { customMessage: 'There are errors in the pipeline.' };
      return (
        <ActionableAlert
          status="error"
          mb="10px"
          actionElement={customError.actionElement}
        >
          {customError.customMessage}
        </ActionableAlert>
      );
    }

    case 'success':
      return null;

    case 'waitingForInput':
      return (
        <ActionableAlert status="warning" mb="10px">
          Upload all required files
        </ActionableAlert>
      );

    case 'waitingForAssignment': {
      const assignmentLinkElement = (
        <Link
          to="/assignments"
          style={{ color: 'blue', textDecoration: 'underline' }}
        >
          Confirm Assignment
        </Link>
      );

      return (
        <ActionableAlert status="warning" actionElement={assignmentLinkElement}>
          Review your assignments.
        </ActionableAlert>
      );
    }

    case 'waitingForVerificationApproval':
      return (
        <ActionableAlert status="warning">Waiting for approval</ActionableAlert>
      );

    case 'expired':
      return (
        <ActionableAlert status="warning">
          This pipeline is expired.
        </ActionableAlert>
      );
    default:
      return finleyAssertUnreachable(status);
  }
};
